import { action, observable } from 'mobx';
import { Helper } from '../../../Helper';
import { ITargetingApplications } from '../../../interfaces/ITargetingApplications';
import { IInstance } from '../../../interfaces/IInstance';
import { ITreeLinkNode } from '../../../interfaces/ITreeLinkNode';
import { IFontSlots } from '../../../interfaces/IFontSlots';

export class TreeLinkStore {

  //////////////////////////////// Component properties //////////////////////////////////

  @observable public environment: "Development" | "Test" | "Production";
  @observable public token: string;
  @observable public tenant: string;
  @observable public highlightColor: string;
  @observable public showLine: boolean;
  @observable public defaultIcon: string;
  @observable public backgroundColor: string;
  @observable public lineColor: string;
  @observable public textColor: string;
  @observable public iconColor: string;
  @observable public dropShadow: boolean;
  @observable public borderRadius: string;
  @observable public showIconOnItems: boolean;
  @observable public isMobile: boolean;
  @observable public hasDarkBackground: boolean;
  @observable public inEditMode: boolean;
  @observable public styles: React.CSSProperties;
  @observable public amountOfFirstLevelItemsToShow: number;
  @observable public openAllLinksExternally: boolean;
  @observable public fontSlots: IFontSlots;

  @action public setEnvironment(environment: "Development" | "Test" | "Production"): void {
    this.environment = environment;
  }

  @action public setToken(token: string): void {
    this.token = token;
  }

  @action public setTenant(tenant: string): void {
    this.tenant = tenant;
  }

  @action public setHighlightColor(highlightColor: string): void {
    this.highlightColor = highlightColor;
  }

  @action public setShowLine(showLine: boolean): void {
    this.showLine = showLine;
  }

  @action public setDefaultIcon(defaultIcon: string): void {
    this.defaultIcon = defaultIcon;
  }

  @action public setBackgroundColor(backgroundColor: string): void {
    this.backgroundColor = backgroundColor;
  }

  @action public setLineColor(lineColor: string): void {
    this.lineColor = lineColor;
  }

  @action public setTextColor(textColor: string): void {
    this.textColor = textColor;
  }

  @action public setIconColor(iconColor: string): void {
    this.iconColor = iconColor;
  }

  @action public setDropShadow(dropShadow: boolean): void {
    this.dropShadow = dropShadow;
  }

  @action public setBorderRadius(borderRadius: string): void {
    this.borderRadius = borderRadius;
  }

  @action public setShowIconOnItems(showIconOnItems: boolean): void {
    this.showIconOnItems = showIconOnItems;
  }

  @action public setIsMobile(isMobile: boolean): void {
    this.isMobile = isMobile;
  }

  @action public setHasDarkBackground(hasDarkBackground: boolean): void {
    this.hasDarkBackground = hasDarkBackground;
  }

  @action public setInEditMode(inEditMode: boolean): void {
    this.inEditMode = inEditMode;
  }

  @action public setStyles(styles: React.CSSProperties): void {
    this.styles = styles;
  }

  @action public setAmountOfFirstLevelItemsToShow(amountOfFirstLevelItemsToShow: number): void {
    this.amountOfFirstLevelItemsToShow = amountOfFirstLevelItemsToShow;
  }

  @action public setOpenAllLinksExternally(openAllLinksExternally: boolean): void {
    this.openAllLinksExternally = openAllLinksExternally;
  }

  @action public setFontSlots(fontSlots: IFontSlots): void {
    this.fontSlots = fontSlots;
  }

  // TreeLink //
  @observable public treeLinkList: ITreeLinkNode[];
  @observable public treeLink: ITreeLinkNode;
  @observable public selectedNode: ITreeLinkNode;
  @observable public selectedNodeOriginal: ITreeLinkNode;
  @observable public originalTreeLink: string;
  @observable public parentNode: ITreeLinkNode;
  @observable public selectedHeader: ITreeLinkNode;
  @observable public isContextMenu: boolean;
  @observable public applicationSettings: ITargetingApplications;
  @observable public instance: IInstance;
  @observable public panelIsOpen: boolean;
  @observable public isCreatingNew: boolean;
  @observable public expandAsDefault: boolean;
  @observable public saving: boolean;
  @observable public isDragging: boolean;
  @observable public editHighlightColor: string;
  @observable public returnPageUrl: boolean;

  @action public setTreeLinkList(treeLinkList: ITreeLinkNode[]): void {
    this.treeLinkList = treeLinkList;
  }

  @action public setTreeLink(treeLink: ITreeLinkNode): void {
    this.treeLink = treeLink;
  }

  @action public setSelectedNode(selectedNode: ITreeLinkNode): void {
    this.selectedNode = selectedNode;
    this.selectedNodeOriginal = JSON.parse(JSON.stringify(selectedNode));
  }

  @action public setOriginalTreeLink(originalTreeLink: string): void {
    this.originalTreeLink = originalTreeLink;
  }

  @action public setParentNode(parentNode: ITreeLinkNode): void {
    this.parentNode = parentNode;
  }

  @action public setSelectedHeader(selectedHeader: ITreeLinkNode): void {
    this.selectedHeader = selectedHeader;
  }

  @action public setIsContextMenu(isContextMenu: boolean): void {
    this.isContextMenu = isContextMenu;
  }

  @action public setApplicationSettings(applicationSettings: ITargetingApplications): void {
    this.applicationSettings = applicationSettings;
  }

  @action public setInstance(instance: IInstance): void {
    this.instance = instance;
  }

  @action public setPanelIsOpen(panelIsOpen: boolean): void {
    this.panelIsOpen = panelIsOpen;
  }

  @action public setIsCreatingNew(isCreatingNew: boolean): void {
    this.isCreatingNew = isCreatingNew;
  }

  @action public setExpandAsDefault(expandAsDefault: boolean): void {
    this.expandAsDefault = expandAsDefault;
  }

  @action public setSaving(saving: boolean): void {
    this.saving = saving;
  }

  @action public setIsDragging(isDragging: boolean): void {
    this.isDragging = isDragging;
  }

  @action public setEditHighlightColor(editHighlightColor: string): void {
    this.editHighlightColor = editHighlightColor;
  }

  @action public setReturnPageUrl(returnPageUrl: boolean): void {
    this.returnPageUrl = returnPageUrl;
  }

  @action private findNode(node: ITreeLinkNode, idNode: string): ITreeLinkNode {
    let selectedNode;
    if (node.config.id === idNode) {
      selectedNode = node;
    } else if (node.children && node.children.length > 0) {
      for (let i = 0; node.children.length > i; i++) {
        selectedNode = this.findNode(node.children[i], idNode);
        if (selectedNode) {
          break;
        }
      }
    }
    return selectedNode;
  }

  @action public setSelectedNodeWithParent(node: ITreeLinkNode, parent: ITreeLinkNode | string, isContextMenu: boolean) {
    this.isCreatingNew = false;
    node.config.isContextMenuExpanded = false;
    node.type = node.type !== undefined && node.type !== null ? node.type : "Text";
    this.selectedNode = node;
    this.selectedNodeOriginal = JSON.parse(JSON.stringify(node));
    this.isContextMenu = isContextMenu;
    if (typeof (parent) === 'string') {
      this.parentNode = this.findNode(this.treeLink, parent);
    } else {
      this.parentNode = parent;
    }
    if (this.selectedNode) {
      this.panelIsOpen = true;
    } else {
      this.panelIsOpen = false;
    }
    this.originalTreeLink = JSON.stringify(this.treeLink);
  }

  @action public createNewNode(parent: ITreeLinkNode) {
    this.isCreatingNew = true;
    this.parentNode = parent;
    this.selectedNode = {
      type: "Text",
      config: {
        id: '',
        nodeLevel: this.parentNode.config.nodeLevel + 1,
        openNewTab: true,
        title: "",
        icon: this.defaultIcon
      },
      targeting: {
        enabled: false
      }
    };
    this.panelIsOpen = true;
    setTimeout(() => {
      if (document.getElementById("IA_treeViewEditPanelTitleField")) {
        document.getElementById("IA_treeViewEditPanelTitleField").focus();
      }
    }, 1000);
  }

  @action public getSelectedNode(node: ITreeLinkNode): ITreeLinkNode {
    this.selectedHeader = node;
    return this.selectedHeader;
  }

  @action public toggleCollapse(node: ITreeLinkNode) {
    const selected = this.findNode(this.treeLink, node.config.id);
    selected.config.isCollapsed = !selected.config.isCollapsed;
  }

  @action public collapseAllContextMenu(node: ITreeLinkNode) {
    node.config.isContextMenuExpanded = false;
    const cb = (n) => {
      n.config.isContextMenuExpanded = false;
      if (n.children) { n.children.forEach(cb); }
    };
    if (node.children && node.children.length > 0) {
      node.children.forEach(cb);
    }
  }

  @action public toggleCollapseContextMenu(node: ITreeLinkNode) {
    if (node.config.isContextMenuExpanded) {
      node.config.isContextMenuExpanded = !node.config.isContextMenuExpanded;
    } else {
      this.collapseAllContextMenu(node);
      node.config.isContextMenuExpanded = true;
    }
  }

  @action private getParentNode(node: ITreeLinkNode): ITreeLinkNode {
    let parent: ITreeLinkNode;
    if (node?.config?.nodeLevel) {
      if (node?.config?.nodeLevel === 1) {
        parent = this.treeLink;
      } else {
        parent = this.getNodeWithId(this.treeLink, node?.config?.parent);
      }
    }
    return parent;
  }

  @action private getNodeWithId(node: ITreeLinkNode, id: string): ITreeLinkNode {
    if (node.children.length > 0) {
      for (let i = 0; i < node.children.length; i++) {
        if (node.children[i].config.id === id) {
          return node.children[i];
        } else {
          if (node.children[i].children) {
            let nodeFound: ITreeLinkNode = this.getNodeWithId(node.children[i], id);
            if (nodeFound) {
              return nodeFound;
            }
          }
        }
      }
    }
  }

  @action public promoteNode(node: ITreeLinkNode) {
    const stringifiedNode = JSON.stringify(node);
    const parent: ITreeLinkNode = this.getParentNode(node);
    if (parent?.config?.nodeLevel === 0) {
      return;
    }
    // Delete 
    let indexToDelete;
    if (parent?.children) {
      parent.children.forEach((child: ITreeLinkNode, index) => {
        if (child.config.id === node.config.id) {
          indexToDelete = index;
        }
      });
      parent.children.splice(indexToDelete, 1);
    }
    // Add
    const grandparent: ITreeLinkNode = this.getParentNode(parent);
    let newIndex;
    grandparent?.children?.forEach((newsibling: ITreeLinkNode, index) => {
      if (newsibling.config.id === node.config.parent) {
        newIndex = index + 1;
      }
    });
    const promotedNode: ITreeLinkNode = Object.assign({}, JSON.parse(stringifiedNode));
    if (promotedNode && grandparent?.children?.length > 0) {
      promotedNode.config.nodeLevel = promotedNode.config.nodeLevel - 1;
      promotedNode.config.parent = parent?.config?.parent;
      if (promotedNode.children) {
        this.promoteChildrenNodes(promotedNode);
      }
      grandparent.children.splice(newIndex, 0, promotedNode);
    }
  }

  private promoteChildrenNodes(node: ITreeLinkNode): void {
    node.children.forEach(child => {
      child.config.nodeLevel = child.config.nodeLevel - 1;
      if (child.children) {
        this.promoteChildrenNodes(child);
      }
    });
  }

  @action public demoteNote(node: ITreeLinkNode) {
    const stringifiedNode = JSON.stringify(node);
    const parent: ITreeLinkNode = this.getParentNode(node);
    // Delete 
    let indexToDelete;
    if (parent?.children) {
      parent.children.forEach((child: ITreeLinkNode, index) => {
        if (child.config.id === node.config.id) {
          indexToDelete = index;
        }
      });
      parent.children.splice(indexToDelete, 1);
    }
    // Add
    const newParentIndex = indexToDelete - 1;
    if (newParentIndex && !parent?.children[newParentIndex].children) {
      parent.children[newParentIndex].children = [];
    }
    const demotedNode: ITreeLinkNode = Object.assign({}, JSON.parse(stringifiedNode));
    if (demotedNode) {
      demotedNode.config.nodeLevel = demotedNode.config.nodeLevel + 1;
      demotedNode.config.parent = parent?.children[newParentIndex].config.id;
      if (demotedNode.children) {
        this.demoteChildrenNodes(demotedNode);
      }
      parent?.children[newParentIndex].children.push(
        demotedNode
      );
    }
    console.log(this.treeLink);
  }

  private demoteChildrenNodes(node: ITreeLinkNode): void {
    node.children.forEach(child => {
      child.config.nodeLevel = child.config.nodeLevel + 1;
      if (child.children) {
        this.demoteChildrenNodes(child);
      }
    });
  }

  @action public canDemote(node: ITreeLinkNode): boolean {
    let canDemote = true;
    if (node.config.nodeLevel === 0) {
      canDemote = false;
    }
    const parent = this.findNode(this.treeLink, node.config.parent);
    if (parent && parent.children) {
      parent.children.forEach((child: ITreeLinkNode, index) => {
        if (child.config.id === node.config.id && index === 0) {
          canDemote = false;
        }
      });
    }
    return canDemote;
  }

  @action public canMoveNode(node: ITreeLinkNode, direction: string) {
    const parent = this.findNode(this.treeLink, node.config.parent);
    if (parent && parent.children) {
      const selectedNodePosition = parent.children.findIndex(x => x.config.id === node.config.id);
      if ((selectedNodePosition === 0 && direction === "up") ||
        parent.children && (selectedNodePosition === parent.children.length - 1) && direction === "down") {
        return false;
      } else {
        return true;
      }
    }
    return false;
  }

  @action public moveNode(node: ITreeLinkNode, direction: string) {
    const selectedNode: ITreeLinkNode = this.findNode(this.treeLink, node.config.id);
    const parent: ITreeLinkNode = this.findNode(this.treeLink, node.config.parent);
    const selectedNodePosition = parent.children.findIndex(x => x.config.id === node.config.id);
    if ((selectedNodePosition === 0 && direction === "up") ||
      parent.children && (selectedNodePosition === parent.children.length - 1) && direction === "down") {
      return;
    }
    const targetNodePosition = direction && direction === "down" ? selectedNodePosition + 1 : selectedNodePosition - 1;
    const targetNode = parent.children[targetNodePosition];
    parent.children[targetNodePosition] = selectedNode;
    parent.children[selectedNodePosition] = targetNode;
  }

  @action public addNode() {
    this.selectedNode.config.parent = this.parentNode ? this.parentNode.config.id : undefined;
    const node = this.selectedNode;
    if (!this.selectedNode.config.id || this.selectedNode.config.id === "") {
      node.config.id = Helper.getRandomStringKey();
    }
    node.children = [];
    node.config.isContextMenuExpanded = false;
    if (!this.parentNode.children) {
      this.parentNode.children = [];
      this.parentNode.config.isCollapsed = false;
    }
    this.parentNode.children.push(node);
  }

  @action public removeNode(node: ITreeLinkNode) {
    const parent = this.findNode(this.treeLink, node.config.parent);
    if (parent) {
      const selectedNodePosition = parent.children.findIndex(x => x.config.id === node.config.id);
      parent.children.splice(selectedNodePosition, 1);
    }
  }

  @action public removeTree(node: ITreeLinkNode) {
    const parent = this.findNode(this.treeLink, node.config.parent);
    const selectedNodePosition = parent.children.findIndex(x => x.config.id === node.config.id);
    parent.children.splice(selectedNodePosition, 1);
  }

}